// ---------------------------------------------------------------------------
// PDM assignment data model (frontend DTOs).
//
// Mirrors the contract served by the new `PdmAssignmentApi` microservice
// (architecture.md "Key components"). The backend persists stable role IDs and
// deterministic assignment IDs (`partnerId:pdmId:role`) plus a Cosmos ETag used
// for optimistic concurrency (FR-43).
// ---------------------------------------------------------------------------

import type { PdmRole } from '../constants/pdmRoleLabels';

/** A single (person, role) assignment on a partner. */
export interface PdmAssignment {
  /** Deterministic Cosmos id: `partnerId:pdmId:role`. */
  id: string;
  /** Cosmos ETag, sent back as `If-Match` on remove (FR-43). */
  etag: string;
  pdmId: string;
  name: string;
  email: string;
  role: PdmRole;
  /** PDM home region, shown for context in the picker (FR-24). */
  region?: string;
}

/** A partner row with its assignments grouped by role column. */
export interface PartnerAssignmentRow {
  partnerId: string;
  /** Display Location ID in the `LOC-####` form the Excel uses today (FR-15). */
  locationId: string;
  partnerName: string;
  /** Tier display name, e.g. "Silver" (badge). */
  tier: string;
  /** Region code, one of PDM_REGIONS (FR-38). */
  region: string;
  /** Single country for the partner (the Figma shows one Country value). */
  country: string;
  pdm: PdmAssignment[];
  pdmManager: PdmAssignment[];
  regionalLeads: PdmAssignment[];
}

/** A directory entry for the `+ Add` picker (backend quick-select or Graph search). */
export interface PdmDirectoryEntry {
  pdmId: string;
  name: string;
  email: string;
  region?: string;
}

/** Regional leads of a single region. */
export interface RegionalLeadGroup {
  region: string;
  leads: PdmAssignment[];
}

/** Body for `POST /api/pdm/assignments`. */
export interface CreatePdmAssignmentRequest {
  partnerId: string;
  pdmId: string;
  role: PdmRole;
  pdmName: string;
  pdmEmail: string;
}

/** One append-only audit-trail entry (`GET /api/pdmassignmentaudit`). */
export interface PdmAuditRecord {
  id: string;
  /** UTC ISO-8601 timestamp of the change. */
  when: string;
  action: 'assigned' | 'unassigned';
  partnerId: string;
  locationId: string;
  partnerName: string;
  region: string;
  /** Previous person/role — blank for additions. */
  fromPdmName: string | null;
  fromPdmEmail: string | null;
  fromRole: string | null;
  /** New person/role — blank for removals. */
  toPdmName: string | null;
  toPdmEmail: string | null;
  toRole: string | null;
  /** Display name of the actor who made the change ("Changed by"). */
  actorName: string;
  actorEmail: string;
}

/** Query for the server-side paged audit endpoint. */
export interface PdmAuditQuery {
  page: number;
  pageSize: number;
  /** Free-text filter (partner name, PDM name, actor). */
  search?: string;
}

/** One page of audit records plus the total count over the full filtered set. */
export interface PagedPdmAudit {
  records: PdmAuditRecord[];
  total: number;
  page: number;
  pageSize: number;
}

/** Maps a stored role ID to the grouped column key on PartnerAssignmentRow. */
export const ROLE_TO_COLUMN: Record<PdmRole, 'pdm' | 'pdmManager' | 'regionalLeads'> = {
  pdm: 'pdm',
  pdm_manager: 'pdmManager',
  regional_leads: 'regionalLeads',
};
